import type { TranslationKey } from '@heidi/runtime/i18n';

import type { SupportedCountry } from './hooks/useUserCountry';

import APPIcon from './assets/APP.svg';
import DSGVOIcon from './assets/DSGVO.svg';
import GDPRIcon from './assets/GDPR.svg';
import HIPAAIcon from './assets/HIPPA.svg';
import ISOIcon from './assets/ISO.svg';
import NHSIcon from './assets/NHS.svg';
import NZIcon from './assets/NZ.svg';
import PIPEDAIcon from './assets/PIPEDA.svg';
import SOCIcon from './assets/SOC.svg';
import TXRAMPIcon from './assets/TXRAMP.svg';
import UKCAIcon from './assets/UKCA.svg';

type ComplianceType =
  | 'APP'
  | 'NHS'
  | 'ISO'
  | 'NZ'
  | 'PIPEDA'
  | 'SOC'
  | 'UKCA'
  | 'GDPR'
  | 'UKGDPR'
  | 'HIPAA'
  | 'TXRAMP'
  | 'DSGVO';

interface ComplianceBadgeConfig {
  iconSrc: string;
  alt: string;
  text: TranslationKey;
}

const COMPLIANCE_BADGES: Record<ComplianceType, ComplianceBadgeConfig> = {
  APP: { iconSrc: APPIcon, alt: 'APP', text: 'auth.complianceBadges.APP' },
  NHS: { iconSrc: NHSIcon, alt: 'NHS', text: 'auth.complianceBadges.NHS' },
  ISO: { iconSrc: ISOIcon, alt: 'ISO', text: 'auth.complianceBadges.ISO' },
  NZ: { iconSrc: NZIcon, alt: 'NZ Privacy', text: 'auth.complianceBadges.NZ' },
  PIPEDA: { iconSrc: PIPEDAIcon, alt: 'PIPEDA', text: 'auth.complianceBadges.PIPEDA' },
  SOC: { iconSrc: SOCIcon, alt: 'SOC 2', text: 'auth.complianceBadges.SOC' },
  UKCA: { iconSrc: UKCAIcon, alt: 'UKCA', text: 'auth.complianceBadges.UKCA' },
  GDPR: { iconSrc: GDPRIcon, alt: 'GDPR', text: 'auth.complianceBadges.GDPR' },
  UKGDPR: { iconSrc: GDPRIcon, alt: 'UK GDPR', text: 'auth.complianceBadges.UKGDPR' },
  HIPAA: { iconSrc: HIPAAIcon, alt: 'HIPAA', text: 'auth.complianceBadges.HIPAA' },
  TXRAMP: { iconSrc: TXRAMPIcon, alt: 'TX-RAMP', text: 'auth.complianceBadges.TXRAMP' },
  DSGVO: { iconSrc: DSGVOIcon, alt: 'DSGVO', text: 'auth.complianceBadges.DSGVO' },
};

const COUNTRY_COMPLIANCE_MAP: Record<SupportedCountry, ComplianceType[]> = {
  AU: ['APP', 'ISO', 'SOC'],
  NZ: ['NZ', 'ISO', 'SOC'],
  CA: ['PIPEDA', 'ISO', 'SOC'],
  UK: ['NHS', 'ISO', 'UKGDPR', 'UKCA', 'SOC'],
  US: ['HIPAA', 'TXRAMP', 'ISO', 'SOC'],
  DE: ['DSGVO', 'ISO', 'GDPR'],
  EU: ['ISO', 'GDPR', 'SOC'],
  OTHER: ['SOC', 'ISO'],
};

/**
 * Per-country badge sets that apply to the onboarding flow only, overriding
 * `COUNTRY_COMPLIANCE_MAP`. Everything else — the sign-in modal, the sign-up
 * trust badges — keeps the shared set, including the localised German DSGVO
 * wording.
 *
 * German onboarding states the privacy credential once, as GDPR, between the
 * two security certifications, because its split layout would otherwise print
 * DSGVO and GDPR (the same regulation under two names) on one screen.
 */
const ONBOARDING_COMPLIANCE_MAP: Partial<Record<SupportedCountry, ComplianceType[]>> = {
  DE: ['SOC', 'GDPR', 'ISO'],
};

function getComplianceBadgesForCountry(
  country: SupportedCountry
): ComplianceBadgeConfig[] {
  const complianceTypes = COUNTRY_COMPLIANCE_MAP[country];
  return complianceTypes.map((type) => COMPLIANCE_BADGES[type]);
}

function getOnboardingTypesForCountry(country: SupportedCountry): ComplianceType[] {
  return ONBOARDING_COMPLIANCE_MAP[country] ?? COUNTRY_COMPLIANCE_MAP[country];
}

/**
 * Badges for an onboarding logo row — the completion illustration's orbit and
 * the microphone step's footer. Both are order-sensitive: the orbit maps each
 * badge to a fixed angle and the microphone row truncates a long list.
 */
function getOnboardingBadgesForCountry(
  country: SupportedCountry
): ComplianceBadgeConfig[] {
  return getOnboardingTypesForCountry(country).map((type) => COMPLIANCE_BADGES[type]);
}

function getOnboardingContentTypesForCountry(
  country: SupportedCountry
): ComplianceType[] {
  // Keep GDPR in the content set for DE: the preview illustration is
  // `lg:block` only, so below that breakpoint the content panel is the only
  // place the credential appears. PrivacyStep hides the content GDPR chip at
  // `lg` so desktop still does not print it twice.
  return getOnboardingTypesForCountry(country);
}

function getOnboardingContentBadgesForCountry(
  country: SupportedCountry
): ComplianceBadgeConfig[] {
  const contentTypes = getOnboardingContentTypesForCountry(country);
  return contentTypes.map((type) => COMPLIANCE_BADGES[type]);
}

const EU_MEMBER_CODES = new Set([
  'FR',
  'ES',
  'IT',
  'NL',
  'BE',
  'AT',
  'PT',
  'PL',
  'SE',
  'DK',
  'FI',
  'IE',
  'GR',
  'CZ',
  'RO',
  'HU',
  'SK',
  'BG',
  'HR',
  'LT',
  'LV',
  'EE',
  'SI',
  'LU',
  'MT',
  'CY',
]);

/**
 * Maps an ISO 3166-1 alpha-2 country code (e.g. from onboarding form data)
 * to the internal `SupportedCountry` taxonomy used by the compliance system.
 *
 * Notably translates `GB -> UK` and groups EU member states into `EU`.
 */
function mapCountryCodeToSupportedCountry(countryCode?: string): SupportedCountry {
  if (!countryCode) return 'OTHER';
  const code = countryCode.toUpperCase();
  switch (code) {
    case 'AU':
      return 'AU';
    case 'NZ':
      return 'NZ';
    case 'CA':
      return 'CA';
    case 'GB':
      return 'UK';
    case 'US':
      return 'US';
    case 'DE':
      return 'DE';
    default:
      return EU_MEMBER_CODES.has(code) ? 'EU' : 'OTHER';
  }
}

// EEA-only members (not in the EU) that must still be treated as EEA.
const EEA_ONLY_CODES = new Set(['NO', 'IS', 'LI']);

/**
 * Whether a country is in the EU or the wider EEA.
 *
 * Drives which regulatory statement onboarding shows: the EU/EEA reads that
 * Heidi is not a medical device, everywhere else reads the intended-use
 * labelling.
 *
 * `DE` and the EEA-only members (Norway, Iceland, Liechtenstein) are checked
 * explicitly because `EU_MEMBER_CODES` omits `DE` (it has its own compliance
 * bucket) and `mapCountryCodeToSupportedCountry` lumps `NO`/`IS`/`LI` into
 * `OTHER`.
 *
 * `GB` is deliberately excluded — post-Brexit it is neither, and Heidi is a
 * regulated device there. `EU_MEMBER_CODES` has carried no `GB` entry since
 * Brexit, and the tests below have always asserted it.
 */
function isEuEeaCountryCode(countryCode?: string): boolean {
  if (!countryCode) return false;
  const code = countryCode.toUpperCase();
  return code === 'DE' || EU_MEMBER_CODES.has(code) || EEA_ONLY_CODES.has(code);
}

/**
 * Whether the Evidence intended-use statement must be hidden.
 *
 * Do not label Evidence as supporting medical / clinical decisions in Europe
 * (including the UK) or Japan. EU/EEA already swap this accordion for the
 * not-a-medical-device banner; this gate still covers them, GB, and JP so the
 * intended-use accordion drops only the Evidence paragraph. AU (and other
 * non-European markets) keep both statements.
 */
function isEvidenceRestrictedCountryCode(countryCode?: string): boolean {
  if (!countryCode) return false;
  const code = countryCode.toUpperCase();
  return code === 'JP' || code === 'GB' || isEuEeaCountryCode(code);
}

const SECURITY_TYPES: ComplianceType[] = ['ISO', 'SOC'];
const REGIONAL_TYPES: ComplianceType[] = [
  'APP',
  'NZ',
  'PIPEDA',
  'NHS',
  'HIPAA',
  'GDPR',
  'UKGDPR',
  'DSGVO',
];
const CERTIFICATION_TYPES: ComplianceType[] = ['UKCA', 'TXRAMP'];

interface PillarBadges {
  security: ComplianceBadgeConfig[];
  regional: ComplianceBadgeConfig[];
  certifications: ComplianceBadgeConfig[];
}

/**
 * Buckets the country's badge list into the three pillars used by the
 * onboarding completion screen: security certifications, regional privacy
 * frameworks, and extra country-specific certifications.
 */
function getPillarBadges(country: SupportedCountry): PillarBadges {
  const types = getOnboardingContentTypesForCountry(country);
  const pick = (filter: ComplianceType[]): ComplianceBadgeConfig[] =>
    types.filter((t) => filter.includes(t)).map((t) => COMPLIANCE_BADGES[t]);
  return {
    security: pick(SECURITY_TYPES),
    regional: pick(REGIONAL_TYPES),
    certifications: pick(CERTIFICATION_TYPES),
  };
}

export {
  COMPLIANCE_BADGES,
  COUNTRY_COMPLIANCE_MAP,
  getComplianceBadgesForCountry,
  getOnboardingBadgesForCountry,
  getOnboardingContentBadgesForCountry,
  getPillarBadges,
  isEuEeaCountryCode,
  isEvidenceRestrictedCountryCode,
  mapCountryCodeToSupportedCountry,
};
export type { ComplianceBadgeConfig, ComplianceType, PillarBadges };
